<template>
    <v-card flat tile class="brands-table-card">
        <div v-if="minimal" class="top-brands-panel">
            <!-- <div class="tb-head d-flex align-center justify-space-between">
                <span class="tb-head-label">Brand Snapshot</span>
                <span class="tb-head-date">{{ latestValueDateLabel }}</span>
            </div> -->

            <div v-if="yourBrandRow" class="tb-row tb-row--brand">
                <div class="tb-left">
                    <span class="tb-dot" :style="brandDotStyle(yourBrandRow)"></span>
                    <div class="tb-name-wrap">
                        <span class="tb-name">
                            {{ yourBrandRow.canonicalName }}
                            <span class="tb-you">(Primary Brand)</span>
                        </span>
                        <span class="tb-meta">Rank {{ getVisibilityRank(yourBrandRow) != null ? `#${getVisibilityRank(yourBrandRow)}` : "-" }}</span>
                    </div>
                </div>
                <div class="tb-right">
                    <span class="tb-value">{{ formatMinimalVisibility(yourBrandRow) }}</span>
                    <span v-if="getMinimalDelta(yourBrandRow) != null" class="tb-delta" :class="deltaClass(getMinimalDelta(yourBrandRow))">
                        <v-icon x-small class="mr-1">{{ getMinimalDelta(yourBrandRow) > 0 ? "mdi-arrow-up" : getMinimalDelta(yourBrandRow) < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                        {{ formatMinimalDelta(getMinimalDelta(yourBrandRow)) }}
                    </span>
                </div>
            </div>

            <div v-if="competitorRows.length" class="tb-divider-label">Market Peers</div>

            <div v-for="item in pagedCompetitorRows" :key="getBrandKey(item)" class="tb-row tb-row--peer">
                <div class="tb-left">
                    <span class="tb-dot" :style="brandDotStyle(item)"></span>
                    <div class="tb-name-wrap">
                        <span class="tb-name">{{ item.canonicalName }}</span>
                        <span class="tb-meta">Rank {{ getVisibilityRank(item) != null ? `#${getVisibilityRank(item)}` : "-" }}</span>
                    </div>
                </div>
                <div class="tb-right">
                    <span class="tb-value">{{ formatMinimalVisibility(item) }}</span>
                    <span v-if="getMinimalDelta(item) != null" class="tb-delta" :class="deltaClass(getMinimalDelta(item))">
                        <v-icon x-small class="mr-1">{{ getMinimalDelta(item) > 0 ? "mdi-arrow-up" : getMinimalDelta(item) < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                        {{ formatMinimalDelta(getMinimalDelta(item)) }}
                    </span>
                </div>
            </div>

            <div v-if="competitorTotalPages > 1" class="tb-pagination-wrap d-flex align-center justify-space-between">
                <v-btn small text :disabled="minimalPeersPage <= 1" @click="minimalPeersPage--">
                    <v-icon left x-small>mdi-chevron-left</v-icon>
                    Prev
                </v-btn>
                <span class="tb-pagination-status">{{ minimalPeersPage }} / {{ competitorTotalPages }}</span>
                <v-btn small text :disabled="minimalPeersPage >= competitorTotalPages" @click="minimalPeersPage++">
                    Next
                    <v-icon right x-small>mdi-chevron-right</v-icon>
                </v-btn>
            </div>

            <div v-if="!yourBrandRow && !competitorRows.length" class="tb-empty">No brands available</div>
        </div>

        <v-data-table
            v-else
            class="brands-table elevation-0"
            :headers="headers"
            :items="displayedBrands"
            :custom-sort="customSort"
            :loading="loading"
            :sort-desc.sync="sortDesc"
            :sort-by.sync="sortBy"
            :items-per-page.sync="itemsPerPage"
            :footer-props="footerProps"
            :page.sync="page"
            :hide-default-footer="false"
            fixed-header
            :height="'70vh'">
            <!-- Header -->
            <template v-slot:header.visibility="{ header }">
                <span class="bt-header-cell" :style="{ width: header.width }">
                    <span class="bt-header-label">
                        {{ header.text }}
                        <v-tooltip max-width="320" top :disabled="!header.tooltip">
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" class="questionIcon" v-if="header.tooltip" x-small>mdi-help-circle-outline</v-icon>
                            </template>
                            <span>{{ header.tooltip }}</span>
                        </v-tooltip>
                        <v-tooltip max-width="280" top>
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" x-small icon class="bt-sort-mode-btn" :class="{ active: visibilitySortMode === 'delta' }" @click.stop="toggleVisibilitySortMode">
                                    <v-icon x-small>{{ getSortModeIcon("visibility", visibilitySortMode) }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ visibilitySortMode === "delta" ? "Sort mode: visibility change (%)" : "Sort mode: absolute visibility" }}</span>
                        </v-tooltip>
                    </span>
                </span>
            </template>

            <template v-slot:header.sentimentAvg="{ header }">
                <span class="bt-header-cell" :style="{ width: header.width }">
                    <span class="bt-header-label">
                        {{ header.text }}
                        <v-tooltip max-width="320" top :disabled="!header.tooltip">
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" class="questionIcon" v-if="header.tooltip" x-small>mdi-help-circle-outline</v-icon>
                            </template>
                            <span>{{ header.tooltip }}</span>
                        </v-tooltip>
                        <v-tooltip max-width="280" top>
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" x-small icon class="bt-sort-mode-btn" :class="{ active: sentimentSortMode === 'delta' }" @click.stop="toggleSentimentSortMode">
                                    <v-icon x-small>{{ getSortModeIcon("sentiment", sentimentSortMode) }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ sentimentSortMode === "delta" ? "Sort mode: sentiment change (%)" : "Sort mode: absolute sentiment" }}</span>
                        </v-tooltip>
                    </span>
                </span>
            </template>

            <template v-for="(header_entry, index) in headers.filter((h) => !['visibility', 'sentimentAvg'].includes(h.value))" v-slot:[`header.${header_entry.value}`]="{ header }">
                <span :key="index" class="bt-header-cell" :style="{ width: header.width }">
                    <v-tooltip max-width="260" top :disabled="!header.tooltip">
                        <template v-slot:activator="{ on }">
                            <span v-on="on" class="bt-header-label">
                                {{ header.text }}
                                <v-icon class="questionIcon" v-if="header.tooltip" x-small>mdi-help-circle-outline</v-icon>
                            </span>
                        </template>
                        <span>{{ header.tooltip }}</span>
                    </v-tooltip>
                </span>
            </template>

            <!-- Brand Name -->
            <template v-slot:item.canonicalName="{ item }">
                <div class="brand-cell">
                    <div class="brand-avatar">
                        <template v-if="getBrandFaviconDomain(item)">
                            <img :src="'https://s2.googleusercontent.com/s2/favicons?domain=' + getBrandFaviconDomain(item) + '&sz=128'" :alt="item.canonicalName" />
                        </template>
                        <template v-else>
                            <span class="brand-avatar-placeholder">
                                {{ item.canonicalName ? item.canonicalName.charAt(0).toUpperCase() : "?" }}
                            </span>
                        </template>
                    </div>
                    <div class="brand-info">
                        <span class="brand-name">{{ item.canonicalName }}</span>
                        <!-- <div class="brand-mentions" v-if="item.mentions && item.mentions.length">
                            {{ item.mentions.slice(0, 2).join(", ") }}
                            <span v-if="item.mentions.length > 2" class="more">+{{ item.mentions.length - 2 }}</span>
                        </div> -->
                    </div>
                </div>
            </template>

            <!-- Visibility -->
            <template v-slot:item.visibility="{ item }">
                <div class="metric-wrapper">
                    <span class="metric-value">
                        {{
                            item.visibilityPercent
                                ? item.visibilityPercent.toFixed(2)
                                : item.responsesWithMention != null && totalPrompts
                                ? ((item.responsesWithMention / totalPrompts) * 100).toFixed(2)
                                : "-"
                        }}%
                    </span>
                    <template v-if="item.visibilityPercentOld != null">
                        <span v-for="rel in [getVisibilityRelativeChange(item)]" :key="rel.key" class="delta-chip" :class="{ up: rel.value > 0, down: rel.value < 0 }">
                            <v-icon x-small left v-if="rel.value !== 0 && rel.finite">
                                {{ rel.value > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                            </v-icon>
                            <span v-if="rel.finite"> {{ rel.value > 0 ? "+" : "" }}{{ rel.value.toFixed(2) }}% </span>
                            <span v-else>∞</span>
                        </span>
                    </template>
                </div>
            </template>

            <!-- Rank (by Visibility) -->
            <template v-slot:item.relevanceRank="{ item }">
                <div class="metric-wrapper">
                    <span class="metric-value">
                        {{ getVisibilityRank(item) != null ? `#${getVisibilityRank(item)}` : "-" }}
                    </span>
                    <template v-if="getVisibilityRankDelta(item) !== null">
                        <span class="delta-chip" :class="{ up: getVisibilityRankDelta(item) > 0, down: getVisibilityRankDelta(item) < 0 }">
                            <v-icon x-small left v-if="getVisibilityRankDelta(item) !== 0">
                                {{ getVisibilityRankDelta(item) > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                            </v-icon>
                            {{ getVisibilityRankDelta(item) > 0 ? "+" : "" }}{{ getVisibilityRankDelta(item) }}
                        </span>
                    </template>
                </div>
            </template>

            <!-- Sentiment -->
            <template v-slot:item.sentimentAvg="{ item }">
                <div class="metric-wrapper sentiment-wrapper">
                    <span class="metric-value">{{ formatSentimentValue(item.sentimentAvg) }}</span>
                    <template v-if="getSentimentDelta(item) !== null">
                        <span class="delta-chip" :class="{ up: getSentimentDelta(item) > 0, down: getSentimentDelta(item) < 0 }">
                            <v-icon x-small left v-if="getSentimentDelta(item) !== 0">
                                {{ getSentimentDelta(item) > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                            </v-icon>
                            {{ getSentimentDelta(item) > 0 ? "+" : "" }}{{ getSentimentDelta(item).toFixed(2) }}%
                        </span>
                    </template>
                </div>
            </template>

            <!-- Mentions -->
            <template v-slot:item.responsesWithMention="{ item }">
                <div class="metric-wrapper">
                    <span class="mentions-chip">
                        {{ item.responsesWithMention }}
                    </span>
                    <template v-if="getMentionsDelta(item) !== null">
                        <span class="delta-chip" :class="{ up: getMentionsDelta(item) > 0, down: getMentionsDelta(item) < 0 }">
                            <v-icon x-small left v-if="getMentionsDelta(item) !== 0">
                                {{ getMentionsDelta(item) > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                            </v-icon>
                            {{ getMentionsDelta(item) > 0 ? "+" : "" }}{{ getMentionsDelta(item) }}
                        </span>
                    </template>
                </div>
            </template>

            <!-- Entity -->
            <template v-slot:item.entity="{ item }">
                <v-btn v-if="item.metadata && item.metadata.mid" x-small outlined color="primary" class="entity-btn" @click.stop="openGoogleEntity(item.metadata.mid)">
                    <v-icon x-small left>mdi-open-in-new</v-icon>
                    Entity
                </v-btn>
            </template>

            <!-- Date -->
            <template v-slot:item.importDate="{ item }">
                <span class="date-cell">
                    {{ formatDate(item.importDate) }}
                </span>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
    import { resolveBrandColor } from "../../utils/brandColor"

    export default {
        name: "BrandsTable",
        props: {
            brands: { type: Array, required: true },
            mode: { type: String },
            loading: { type: Boolean, default: false },
            totalPrompts: { type: Number, required: true },
            minimal: { type: Boolean, default: false },
            project: { type: Object },
            search: { type: String, default: "" },
            sentimentAvailable: { type: Boolean, default: false },
            brandColors: { type: Object, default: () => ({}) },
            isActive: { type: Boolean, default: false }
        },
        data() {
            return {
                sortDesc: true,
                sortBy: "responsesWithMention",
                visibilitySortMode: "absolute",
                sentimentSortMode: "absolute",
                itemsPerPage: this.minimal ? 5 : 25,
                page: 1,
                minimalPeersPage: 1
            }
        },
        computed: {
            yourBrandToken() {
                return String(this.project?.brand?.brand_name || "")
                    .trim()
                    .toLowerCase()
            },
            minimalSortedBrands() {
                const list = Array.isArray(this.brands) ? [...this.brands] : []
                return list.sort((a, b) => {
                    const av = this.getCurrentVisibilityValue(a)
                    const bv = this.getCurrentVisibilityValue(b)
                    return (bv || 0) - (av || 0)
                })
            },
            yourBrandRow() {
                const token = this.yourBrandToken
                if (!token) return null
                return this.minimalSortedBrands.find((item) => this.isYourBrand(item)) || null
            },
            competitorRows() {
                return this.minimalSortedBrands.filter((item) => !this.isYourBrand(item))
            },
            competitorsPerPage() {
                return Math.max(1, this.itemsPerPage - (this.yourBrandRow ? 1 : 0))
            },
            competitorTotalPages() {
                return Math.max(1, Math.ceil(this.competitorRows.length / this.competitorsPerPage))
            },
            pagedCompetitorRows() {
                const start = (this.minimalPeersPage - 1) * this.competitorsPerPage
                return this.competitorRows.slice(start, start + this.competitorsPerPage)
            },
            latestValueDateLabel() {
                const first = this.minimalSortedBrands.find((b) => b?.importDate)
                if (!first?.importDate) return ""
                try {
                    return new Date(first.importDate).toLocaleString("de-DE", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit"
                    })
                } catch (e) {
                    return ""
                }
            },
            changeValue(item) {
                if (item.responsesWithMention != null && item.docsWithMentionOld != null && this.totalPrompts) {
                    const current = (item.responsesWithMention / this.totalPrompts) * 100
                    const old = (item.docsWithMentionOld / this.totalPrompts) * 100
                    return current - old
                }
                return 0
            },
            footerProps() {
                if (this.minimal) return {} // kein Standard-Footer
                return {
                    itemsPerPageOptions: [10, 25, 50, 100],
                    showFirstLastPage: true
                }
            },
            displayedBrands() {
                if (!this.minimal) return this.brands
                const start = (this.page - 1) * this.itemsPerPage
                return this.brands.slice(start, start + this.itemsPerPage)
            },
            totalPages() {
                if (this.minimal) {
                    return Math.max(1, Math.ceil(this.brands.length / this.itemsPerPage))
                }
                // Standard-Footer (Server/normal) kann alternativ andere Basis haben
                return Math.max(1, Math.ceil(this.brands.length / this.itemsPerPage))
            },
            headers() {
                if (this.minimal) {
                    let h = [
                        {
                            text: "Name",
                            value: "canonicalName",
                            tooltip: "The official brand name as recognized in the dataset."
                        },
                        {
                            text: "Visibility (%)",
                            value: "visibility",
                            tooltip: "The percentage of prompts in which this brand was mentioned at least once.",
                            align: "end"
                        }
                    ]
                    if (this.mode === "Relevance") {
                        h[1] = {
                            text: "Rank",
                            value: "relevanceRank",
                            tooltip: "The brand rank based on visibility (#1 = highest visibility).",
                            align: "end"
                        }
                    }
                    return h
                }
                let header = [
                    {
                        text: "Name",
                        value: "canonicalName",
                        tooltip: "The official brand name as recognized in the dataset."
                    },
                    {
                        text: "Rank",
                        value: "relevanceRank",
                        tooltip: "The brand rank based on visibility (#1 = highest visibility).",
                        align: "end"
                    },
                    {
                        text: "Visibility (%)",
                        value: "visibility",
                        tooltip: "The percentage of all prompts in which this brand was mentioned at least once. This indicates how visible the brand is across all responses.",
                        align: "end"
                    },
                    // {
                    //     text: "Sentiment (%)",
                    //     value: "sentimentAvg",
                    //     tooltip: "Average sentiment score for this brand across all responses, scaled from -100 (very negative) to +100 (very positive).",
                    //     align: "end"
                    // },
                    {
                        text: "Mentions",
                        value: "responsesWithMention",
                        tooltip: "The number of responses in which this brand was mentioned at least once. Each response is counted only once, even if the brand appears multiple times within it.",
                        align: "end"
                    }

                    // {
                    //     text: "Relevance (avg)",
                    //     value: "salienceAvg",
                    //     tooltip: "The average relevance score (salience) of this brand across all responses where it was mentioned. Higher values mean the brand was more central to the response.",
                    //     align: "end"
                    // },

                    // {
                    //     text: "Entity",
                    //     value: "entity",
                    //     tooltip: "A link to the recognized entity in Google's Knowledge Graph, if available."
                    // }
                    // {
                    //     text: "First Appearance",
                    //     value: "importDate",
                    //     tooltip: "The date when this brand was first detected in the chat responses.",
                    //     align: "end"
                    // }
                ]
                if (this.sentimentAvailable) {
                    header.splice(3, 0, {
                        text: "Sentiment (%)",
                        value: "sentimentAvg",
                        tooltip: "Average sentiment score for this brand across all responses, scaled from -100 (very negative) to +100 (very positive).",
                        align: "end"
                    })
                }
                return header
            }
        },
        watch: {
            search() {
                if (this.isActive) this.$emit("searchUpdated")
            },
            isActive(newVal) {
                if (newVal && this.search) this.$emit("searchUpdated")
            },
            minimal: {
                immediate: true,
                handler(val) {
                    this.itemsPerPage = val ? 5 : 25
                    this.page = 1
                    this.minimalPeersPage = 1
                }
            },
            brands() {
                if (this.page > this.totalPages) this.page = this.totalPages
                if (this.minimalPeersPage > this.competitorTotalPages) this.minimalPeersPage = this.competitorTotalPages
            },
            mode: {
                immediate: true,
                handler(newMode) {
                    if (this.minimal) {
                        this.sortBy = newMode === "Relevance" ? "relevanceRank" : "visibility"
                        this.sortDesc = newMode !== "Relevance"
                    } else {
                        this.sortBy = newMode === "Relevance" ? "relevanceRank" : "responsesWithMention"
                        this.sortDesc = newMode !== "Relevance"
                    }
                }
            }
        },
        methods: {
            isYourBrand(item) {
                const token = this.yourBrandToken
                if (!token || !item) return false
                const canonical = String(item?.canonicalName || "")
                    .trim()
                    .toLowerCase()
                const brandName = String(item?.brand_name || "")
                    .trim()
                    .toLowerCase()
                const mentions = Array.isArray(item?.mentions)
                    ? item.mentions.map((m) =>
                          String(typeof m === "string" ? m : m?.text?.content || m?.content || m?.name || "")
                              .trim()
                              .toLowerCase()
                      )
                    : []
                return canonical === token || brandName === token || mentions.includes(token)
            },
            getCurrentVisibilityValue(item) {
                if (typeof item?.visibilityPercent === "number") return item.visibilityPercent
                if (item?.responsesWithMention != null && this.totalPrompts) return (item.responsesWithMention / this.totalPrompts) * 100
                return null
            },
            getPreviousVisibilityValue(item) {
                if (typeof item?.visibilityPercentOld === "number") return item.visibilityPercentOld
                if (item?.responsesWithMentionOld != null && this.totalPrompts) return (item.responsesWithMentionOld / this.totalPrompts) * 100
                return null
            },
            formatMinimalVisibility(item) {
                const value = this.getCurrentVisibilityValue(item)
                if (value == null) return "-"
                return `${value.toFixed(1)}%`
            },
            getMinimalDelta(item) {
                const current = this.getCurrentVisibilityValue(item)
                const previous = this.getPreviousVisibilityValue(item)
                if (current == null || previous == null) return null
                if (previous === 0) {
                    if (current === 0) return 0
                    return Number.POSITIVE_INFINITY
                }
                return ((current - previous) / previous) * 100
            },
            formatMinimalDelta(delta) {
                const n = Number(delta)
                if (!Number.isFinite(n)) return "∞%"
                return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`
            },
            deltaClass(delta) {
                return {
                    up: delta > 0,
                    down: delta < 0
                }
            },
            brandDotStyle(item) {
                const key = String(item?.canonicalName || item?.brand_name || this.getBrandKey(item) || "brand")
                return { background: resolveBrandColor(key, this.brandColors) }
            },
            getBrandKey(item) {
                return (item?._id && item._id.toString()) || item?.canonicalName || JSON.stringify(item?.mentions || [])
            },
            getVisibilityRank(item) {
                // Rang kollisionssicher aus dem eigenen Visibility-Wert der Zeile
                // berechnen (nicht über eine Key-Map, da im Average-Modus mehrere
                // Brands denselben canonicalName-Key teilen können und sich sonst
                // gegenseitig überschreiben).
                const value = this.getCurrentVisibilityValue(item)
                if (value == null) return null
                const list = Array.isArray(this.brands) ? this.brands : []
                let higher = 0
                for (const b of list) {
                    const v = this.getCurrentVisibilityValue(b)
                    if (v != null && v > value) higher++
                }
                return higher + 1
            },
            getVisibilityRankDelta(item) {
                const cur = this.getCurrentVisibilityValue(item)
                const prev = this.getPreviousVisibilityValue(item)
                if (cur == null || prev == null) return null
                const list = Array.isArray(this.brands) ? this.brands : []
                let higherCur = 0
                let higherPrev = 0
                for (const b of list) {
                    const cv = this.getCurrentVisibilityValue(b)
                    if (cv != null && cv > cur) higherCur++
                    const pv = this.getPreviousVisibilityValue(b)
                    if (pv != null && pv > prev) higherPrev++
                }
                // positives Delta = Rang verbessert (nach oben bewegt)
                return higherPrev - higherCur
            },
            getVisibilityDeltaPercent(item) {
                const rel = this.getVisibilityRelativeChange(item)
                if (!rel || rel.finite === false) return null
                const val = Number(rel.value)
                return Number.isFinite(val) ? val : null
            },
            getMentionsDelta(item) {
                const current = typeof item?.responsesWithMention === "number" ? item.responsesWithMention : null
                const previous = typeof item?.responsesWithMentionOld === "number" ? item.responsesWithMentionOld : typeof item?.docsWithMentionOld === "number" ? item.docsWithMentionOld : null
                if (current == null || previous == null) return null
                return current - previous
            },
            toggleVisibilitySortMode() {
                this.visibilitySortMode = this.visibilitySortMode === "absolute" ? "delta" : "absolute"
            },
            toggleSentimentSortMode() {
                this.sentimentSortMode = this.sentimentSortMode === "absolute" ? "delta" : "absolute"
            },
            getSortModeIcon(metric, mode) {
                if (metric === "visibility") {
                    return mode === "delta" ? "mdi-trending-up" : "mdi-swap-vertical"
                }
                if (metric === "sentiment") {
                    return mode === "delta" ? "mdi-trending-up" : "mdi-swap-vertical"
                }
                return mode === "delta" ? "mdi-trending-up" : "mdi-swap-vertical"
            },
            getSortMetricValue(item, key) {
                if (!item) return null
                if (key === "visibility") {
                    return this.visibilitySortMode === "delta" ? this.getVisibilityDeltaPercent(item) : this.getCurrentVisibilityValue(item)
                }
                if (key === "relevanceRank") return this.getVisibilityRank(item)
                if (key === "responsesWithMention") return item.responsesWithMention ?? null
                if (key === "sentimentAvg") {
                    return this.sentimentSortMode === "delta" ? this.getSentimentDelta(item) : typeof item.sentimentAvg === "number" ? item.sentimentAvg : null
                }
                if (key === "importDate") return item.importDate ? new Date(item.importDate).getTime() : null

                const val = item[key]
                if (typeof val === "number") return val
                if (typeof val === "string") return val.toLowerCase()
                if (val instanceof Date) return val.getTime()
                return val ?? null
            },
            customSort(items, sortBy, sortDesc) {
                const list = Array.isArray(items) ? [...items] : []

                const sortKey = Array.isArray(sortBy) ? sortBy[0] : sortBy
                const desc = Array.isArray(sortDesc) ? !!sortDesc[0] : !!sortDesc
                if (!sortKey) return list

                return list.sort((a, b) => {
                    const av = this.getSortMetricValue(a, sortKey)
                    const bv = this.getSortMetricValue(b, sortKey)

                    const aMissing = av == null || Number.isNaN(av)
                    const bMissing = bv == null || Number.isNaN(bv)
                    if (aMissing && bMissing) return 0
                    if (aMissing) return 1
                    if (bMissing) return -1

                    if (typeof av === "string" || typeof bv === "string") {
                        const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base", numeric: true })
                        return desc ? -cmp : cmp
                    }

                    const diff = Number(av) - Number(bv)
                    if (!Number.isFinite(diff) || diff === 0) return 0
                    return desc ? -diff : diff
                })
            },
            getVisibilityRelativeChange(item) {
                const oldC = item.visibilityPercentOld
                const newC = item.visibilityPercent
                if (oldC == null || newC == null) return { key: "visRel", value: 0, finite: true }
                if (oldC === 0) {
                    if (newC === 0) return { key: "visRel", value: 0, finite: true }
                    return { key: "visRel", value: 0, finite: false } // ∞
                }
                const rel = ((newC - oldC) / oldC) * 100
                return { key: "visRel", value: rel, finite: true }
            },
            openGoogleEntity(mid) {
                const url = `https://www.google.com/search?kgmid=${encodeURIComponent(mid)}`
                window.open(url, "_blank", "noopener")
            },
            formatDate(date) {
                if (!date) return ""
                return new Date(date).toLocaleDateString()
            },
            formatSentimentValue(value) {
                if (typeof value !== "number" || Number.isNaN(value)) return "-"
                const pct = Math.max(-100, Math.min(100, value * 100))
                const normalized = Math.abs(pct) < 0.05 ? 0 : pct
                return `${normalized.toFixed(0)}`
            },
            sentimentChipClass(value) {
                if (typeof value !== "number") return "is-neutral"
                if (value > 0.05) return "is-positive"
                if (value < -0.05) return "is-negative"
                return "is-neutral"
            },
            getSentimentDelta(item) {
                if (typeof item !== "object" || item === null) return null
                const current = typeof item.sentimentAvg === "number" ? item.sentimentAvg : null
                const previous = typeof item.sentimentAvgOld === "number" ? item.sentimentAvgOld : null
                if (current == null || previous == null) return null
                const base = Math.abs(previous)
                if (base < 1e-6) return null
                return ((current - previous) / base) * 100
            },
            getBrandFaviconDomain(item) {
                // 1. officialWebsite bevorzugen
                if (item.metadata && item.metadata.officialWebsite) {
                    return item.metadata.officialWebsite.replace(/^https?:\/\//, "").split("/")[0]
                }
                // 2. Domains aus mentions extrahieren, Duplikate vermeiden
                if (item.mentions && item.mentions.length) {
                    // Alle Domains normalisieren (ohne www. und ohne http(s))
                    const domains = item.mentions
                        .map(
                            (m) =>
                                m
                                    .replace(/^https?:\/\//, "")
                                    .replace(/^www\./, "")
                                    .split("/")[0]
                        )
                        .filter((m) => /^[\w-]+\.[a-z]{2,}$/.test(m))
                    // Nur eindeutige Domains
                    const uniqueDomains = [...new Set(domains)]
                    if (uniqueDomains.length) return uniqueDomains[0]
                    const firstMention = typeof item.mentions[0] === "string" ? item.mentions[0].trim() : ""
                    let fallbackMention = firstMention
                    if (firstMention.includes(" ") && item.mentions.length > 1) {
                        const nextMention = item.mentions.slice(1).find((m) => typeof m === "string" && m.trim())
                        if (nextMention) fallbackMention = nextMention.trim()
                    }
                    return fallbackMention + "." + this.project.langloc.country_code.toLowerCase()
                }
                return null
            },
            exportTableData() {
                // Hole die sichtbaren Header (ohne Aktionen)
                const headers = this.headers.map((h) => h.text)
                // Hole die sichtbaren Daten (this.brands)
                const rows = this.brands.map((item) =>
                    this.headers.map((h) => {
                        let val = item[h.value]
                        // Arrays als Komma-getrennte Strings
                        if (Array.isArray(val)) return `"${val.join(", ")}"`
                        // Objekte als JSON
                        if (typeof val === "object" && val !== null) return `"${JSON.stringify(val)}"`
                        if (h.value === "sentimentAvg") {
                            return typeof item.sentimentAvg === "number" ? (item.sentimentAvg * 100).toFixed(1) + "%" : "-"
                        }
                        // Zahlen und Strings escapen
                        if (typeof val === "string") return `"${val.replace(/"/g, '""')}"`
                        if (typeof val === "number") return val
                        // Spezialfälle für berechnete Felder
                        if (h.value === "visibility" && item.responsesWithMention != null && this.totalPrompts) {
                            return ((item.responsesWithMention / this.totalPrompts) * 100).toFixed(2) + "%"
                        }
                        if (h.value === "relevanceRank") {
                            const rank = this.getVisibilityRank(item)
                            return rank != null ? `#${rank}` : "-"
                        }
                        if (h.value === "importDate" && item.importDate) {
                            return new Date(item.importDate).toLocaleDateString()
                        }
                        return val !== undefined && val !== null ? val : ""
                    })
                )
                return [headers, ...rows].map((e) => e.join(";")).join("\n")
            },
            prevPage() {
                if (this.page > 1) this.page--
            },
            nextPage() {
                if (this.page < this.totalPages) this.page++
            }
        }
    }
</script>

<style>
    .top-brands-panel {
        padding: 8px 10px 10px;
        /* background: #f5f6f8; */
        border-radius: 12px;
    }
    .tb-head {
        padding: 4px 6px 8px;
        color: #7a7f87;
        font-size: 11px;
        letter-spacing: 0.2px;
        text-transform: none;
    }
    .tb-head-label {
        font-weight: 600;
    }
    .tb-head-date {
        font-weight: 500;
    }
    .tb-divider-label {
        text-align: center;
        margin: 6px 4px;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #9aa0a8;
        border-top: 1px dashed rgba(0, 0, 0, 0.14);
        padding-top: 6px;
    }
    .tb-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 10px;
        padding: 8px 10px;
        margin-bottom: 8px;
    }
    .tb-row--brand {
        border-color: rgba(25, 118, 210, 0.22);
        background: rgba(25, 118, 210, 0.05);
    }
    .tb-row--peer {
        border-left: 3px solid rgba(15, 23, 42, 0.08);
    }
    .tb-left {
        display: inline-flex;
        align-items: center;
        min-width: 0;
        gap: 8px;
    }
    .tb-dot {
        width: 10px;
        height: 10px;
        border-radius: 999px;
        flex: 0 0 10px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        position: relative;
        top: 1px;
    }
    .theme--dark .tb-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .tb-name {
        font-size: 14px;
        font-weight: 600;
        color: #333b46;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 210px;
        line-height: 1.3;
    }
    .tb-name-wrap {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .tb-meta {
        font-size: 11px;
        color: #595959;
        font-weight: 500;
        letter-spacing: 0.1px;
    }
    .tb-you {
        font-size: 12px;
        font-weight: 600;
        color: #2f80ed;
    }
    .tb-right {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }
    .tb-value {
        font-size: 18px;
        font-weight: 700;
        line-height: 1;
        color: #1f2937;
    }
    .tb-delta {
        display: inline-flex;
        align-items: center;
        font-size: 12px;
        font-weight: 700;
        border-radius: 999px;
        padding: 2px 8px;
        background: rgba(107, 114, 128, 0.14);
        color: #6b7280;
    }
    .tb-delta.up {
        background: rgba(30, 150, 70, 0.15);
        color: #1c8c45;
    }
    .tb-delta.down {
        background: rgba(214, 69, 69, 0.15);
        color: #cf3d3d;
    }
    .tb-empty {
        text-align: center;
        padding: 20px 10px;
        color: #8a929d;
        font-size: 13px;
    }
    .tb-pagination-wrap {
        margin-top: 2px;
        margin-bottom: 4px;
        border-top: 1px dashed rgba(0, 0, 0, 0.12);
        padding-top: 6px;
    }
    .tb-pagination-status {
        font-size: 12px;
        color: #7f8792;
        font-weight: 600;
    }

    .theme--dark .top-brands-panel {
        background: #1f2328;
    }
    .theme--dark .tb-head {
        color: #a7adb7;
    }
    .theme--dark .tb-divider-label {
        color: #8f97a3;
        border-top-color: rgba(255, 255, 255, 0.16);
    }
    .theme--dark .tb-row {
        background: #242a31;
        border-color: rgba(255, 255, 255, 0.1);
    }
    .theme--dark .tb-row--brand {
        border-color: rgba(66, 165, 245, 0.35);
        background: rgba(66, 165, 245, 0.12);
    }
    .theme--dark .tb-row--peer {
        border-left-color: rgba(255, 255, 255, 0.12);
    }
    .theme--dark .tb-name {
        color: #e6ebf2;
    }
    .theme--dark .tb-meta {
        color: #9ca7b5;
    }
    .theme--dark .tb-value {
        color: #f3f6fb;
    }
    .theme--dark .tb-you {
        color: #73b6ff;
    }
    .theme--dark .tb-pagination-wrap {
        border-top-color: rgba(255, 255, 255, 0.16);
    }
    .theme--dark .tb-pagination-status {
        color: #a4adba;
    }
    .questionIcon {
        margin-left: 3px;
        vertical-align: middle;
    }
    .brand-placeholder {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 1.1rem;
        background: #fff;
        color: #219653; /* Vuetify green darken-2 oder dein Wunschgrün */
    }
    .dtd {
        font-size: 0.98rem;
    }
    /* --- Table Container --- */
    .brands-table-card {
        border-radius: 14px;
        overflow: hidden;
    }

    /* --- Headers --- */
    .brands-table .bt-header-cell {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #5a6068;
        padding: 0 4px;
    }
    .theme--dark .brands-table .bt-header-cell {
        color: #b9c0c8;
    }
    .brands-table .bt-sort-mode-btn {
        margin-left: 4px;
        width: 22px;
        height: 22px;
        min-width: 22px;
        border-radius: 7px !important;
        background: rgba(15, 23, 42, 0.08) !important;
        border: 1px solid rgba(15, 23, 42, 0.14) !important;
        color: #72808f !important;
        transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
    }
    .brands-table .bt-sort-mode-btn:hover {
        background: rgba(15, 23, 42, 0.13) !important;
        border-color: rgba(15, 23, 42, 0.2) !important;
    }
    .brands-table .bt-sort-mode-btn:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.2);
    }
    .brands-table .bt-sort-mode-btn.active {
        background: rgba(15, 118, 110, 0.14) !important;
        border-color: rgba(15, 118, 110, 0.32) !important;
        color: #0f766e !important;
    }
    .theme--dark .brands-table .bt-sort-mode-btn {
        background: rgba(148, 163, 184, 0.14) !important;
        border-color: rgba(148, 163, 184, 0.28) !important;
        color: #94a3b8 !important;
    }
    .theme--dark .brands-table .bt-sort-mode-btn:hover {
        background: rgba(148, 163, 184, 0.24) !important;
        border-color: rgba(148, 163, 184, 0.36) !important;
    }
    .theme--dark .brands-table .bt-sort-mode-btn.active {
        background: rgba(20, 184, 166, 0.18) !important;
        border-color: rgba(20, 184, 166, 0.36) !important;
        color: #5eead4 !important;
    }
    .brands-table thead th {
        background: linear-gradient(180deg, #fafbfd, #f3f5f7);
        border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    }
    .theme--dark .brands-table thead th {
        background: #202325;
        border-bottom-color: rgba(255, 255, 255, 0.08) !important;
    }

    /* --- Rows --- */
    .brands-table tbody tr {
        transition: background 0.18s, transform 0.12s;
    }
    .brands-table tbody tr:hover {
        background: #f6f9fc;
    }
    .theme--dark .brands-table tbody tr:hover {
        background: #262c30;
    }

    /* Zebra */
    .brands-table tbody tr:nth-child(even) {
        background: #fcfdfe;
    }
    .theme--dark .brands-table tbody tr:nth-child(even) {
        background: #22272a;
    }
    .brands-table tbody tr:hover:nth-child(even) {
        background: #f1f6fa;
    }
    .theme--dark .brands-table tbody tr:hover:nth-child(even) {
        background: #2a3236;
    }

    /* --- Brand Cell --- */
    .brand-cell {
        display: flex;
        align-items: center;
        padding: 2px 0;
    }
    .brand-avatar {
        border: 2px solid #e7e7e7;
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: linear-gradient(135deg, #eef2f5, #ffffff);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        margin-right: 12px;
        margin-left: 12px;
        flex-shrink: 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        position: relative;
    }
    .theme--dark .brand-avatar {
        background: linear-gradient(135deg, #2a2d31, #1f2123);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.45);
    }
    .brand-avatar img {
        width: 26px;
        height: 26px;
        border-radius: 6px;
        object-fit: cover;
        display: block;
    }
    .brand-avatar-placeholder {
        width: 26px;
        height: 26px;
        border-radius: 6px;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        color: #2196f3;
    }
    .theme--dark .brand-avatar-placeholder {
        background: #1f2123;
        color: #64b5f6;
    }
    .brand-info {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
    }
    .brand-name {
        font-weight: 600;
        font-size: 13px;
        color: #222;
    }
    .theme--dark .brand-name {
        color: #eee;
    }
    .brand-mentions {
        font-size: 11px;
        margin-top: 3px;
        color: #6a737d;
    }
    .theme--dark .brand-mentions {
        color: #a0abb3;
    }
    .brand-mentions .more {
        opacity: 0.7;
    }

    /* --- Metrics --- */
    .metric-wrapper {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .sentiment-wrapper {
        min-width: 130px;
    }
    .sentiment-chip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 74px;
        padding: 4px 12px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.2px;
        background: #eceff3;
        color: #4b5259;
        border-left: 4px solid transparent;
        transition: background 0.2s, color 0.2s, border-left-color 0.2s;
    }
    .theme--dark .sentiment-chip {
        background: #2b2f34;
        color: #cfd7df;
    }
    .sentiment-chip.is-positive {
        background: rgba(46, 204, 113, 0.16);
        border-left-color: #2ecc71;
        color: #157a4a;
    }
    .theme--dark .sentiment-chip.is-positive {
        background: rgba(46, 204, 113, 0.24);
        color: #64ff9b;
    }
    .sentiment-chip.is-negative {
        background: rgba(235, 87, 87, 0.18);
        border-left-color: #eb5757;
        color: #a82828;
    }
    .theme--dark .sentiment-chip.is-negative {
        background: rgba(235, 87, 87, 0.26);
        color: #ffb5b0;
    }
    .sentiment-chip.is-neutral {
        border-left-color: rgba(0, 0, 0, 0.16);
    }
    .theme--dark .sentiment-chip.is-neutral {
        border-left-color: rgba(255, 255, 255, 0.22);
    }
    .metric-value {
        font-weight: 600;
        font-size: 13px;
        letter-spacing: 0.3px;
    }
    .delta-chip {
        font-size: 14px;
        /* font-weight: 600; */
        padding: 5px 10px 4px 8px;
        border-radius: 18px;
        background: #eef2f5;
        color: #3a4751;
        display: inline-flex;
        align-items: center;
        line-height: 1.05;
        font-variant-numeric: tabular-nums;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        transition: background 0.25s, color 0.25s;
    }
    .theme--dark .delta-chip {
        background: #343a40;
        color: #ccd3d9;
    }
    .delta-chip.up {
        background: rgba(30, 150, 70, 0.14);
        color: #1e7f34;
    }
    .theme--dark .delta-chip.up {
        background: rgba(30, 150, 70, 0.22);
        color: #6fdc91;
    }
    .delta-chip.down {
        background: rgba(200, 60, 50, 0.14);
        color: #c43d35;
    }
    .theme--dark .delta-chip.down {
        background: rgba(200, 60, 50, 0.22);
        color: #ff9c92;
    }

    /* Mentions count */
    .mentions-chip {
        display: inline-block;
        /* background: #eef2f6; */
        border-radius: 6px;
        padding: 2px 8px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.3px;
    }
    .theme--dark .mentions-chip {
        background: #2b3136;
        color: #d8dee3;
    }

    /* Entity button refine */
    .entity-btn {
        text-transform: none;
        font-weight: 500;
        letter-spacing: 0.2px;
    }

    /* Date */
    .date-cell {
        font-size: 12px;
        opacity: 0.75;
        font-weight: 500;
    }

    /* Scrollbar subtle (optional) */
    .brands-table .v-data-table__wrapper::-webkit-scrollbar {
        width: 10px;
    }
    .brands-table .v-data-table__wrapper::-webkit-scrollbar-track {
        background: transparent;
    }
    .brands-table .v-data-table__wrapper::-webkit-scrollbar-thumb {
        background: #d3d8dd;
        border-radius: 6px;
    }
    .theme--dark .brands-table .v-data-table__wrapper::-webkit-scrollbar-thumb {
        background: #3a3f44;
    }

    /* Minimal Footer */
    .bt-mini-footer {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        background: linear-gradient(180deg, #fafbfc, #f3f5f7);
        font-size: 12px;
        font-weight: 500;
    }
    .theme--dark .bt-mini-footer {
        background: #22272a;
        border-top-color: rgba(255, 255, 255, 0.08);
    }
    .bt-mini-status {
        font-size: 12px;
        letter-spacing: 0.4px;
        opacity: 0.8;
    }
    .bt-mini-status .sep {
        margin: 0 6px;
        opacity: 0.6;
    }
    .mini-table-footer {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        font-size: 12px;
        font-weight: 500;
        /* background: linear-gradient(180deg, #fafbfc, #f3f5f7); */
    }
    .theme--dark .mini-table-footer {
        background: #22272a;
        border-top-color: rgba(255, 255, 255, 0.08);
    }
    .mini-table-footer .mini-status {
        font-size: 12px;
        opacity: 0.8;
    }
    .mini-table-footer .mini-status .sep {
        margin: 0 6px;
        opacity: 0.5;
    }
</style>
